/**
 * O rascunho de uma tela de Game Design — <b>gravado no GG Pilot</b>, não no navegador.
 *
 * <p><b>O que mudou, e por quê.</b> As quatro telas guardavam o que o time escrevia no
 * `localStorage`, e diziam isso na tela. Servia enquanto o conteúdo era hipótese de uma
 * pessoa. Deixou de servir quando ficou claro que é o mesmo trabalho: a conta de
 * Mineração escrita na Personagem é a que a Tier List mostra, e a fórmula de HP que o
 * Theo fecha numa terça é a que o dev lê na quinta. Rascunho que mora num navegador só
 * não é do time — é de quem o digitou, e some com o cache dele.
 *
 * <p><b>Uma página por assunto, e não por tela.</b> As <i>rolagens</i> têm página
 * própria porque a Personagem e a Tier List editam as mesmas dezoito — era assim no
 * `localStorage` (uma chave, duas telas) e continua sendo. Guardá-las dentro do
 * documento de cada tela criaria duas cópias da mesma régua, que é o defeito que o
 * CLAUDE.md manda evitar e que estas telas já pagaram uma vez.
 *
 * <p><b>A migração acontece uma vez, sozinha.</b> Quem já tinha rascunho no navegador o
 * encontra gravado na primeira abertura: quando a página nunca foi escrita no servidor,
 * o gancho semeia do `localStorage` e grava. É por isso que os leitores antigos
 * continuam existindo — não como fonte, mas como ponte.
 *
 * <p><b>Consequência que vale saber:</b> o rascunho passou a ser compartilhado. Duas
 * pessoas escrevendo na mesma seção ao mesmo tempo terminam com o texto de quem parou
 * de digitar por último. Para um documento de trabalho isso é aceitável; para dado de
 * produto não seria.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import * as paginaApi from './paginaApi';
import './rascunhoDeGameDesign.css';

/** Quanto se espera parar de digitar antes de gravar. */
const ATRASO_MS = 1200;

export interface EstadoDoRascunho {
  gravando: boolean;
  erroAoGravar: unknown;
  atualizadoPor?: string;
}

/**
 * Liga o estado de uma tela a uma página gravada no servidor.
 *
 * @param chave o nome da página (`personagem`, `combate`, `tier-list`, `rolagens`)
 * @param interpretar constrói o valor da tela a partir do que estava guardado. É o
 *        mesmo encaixe-sobre-o-branco que os leitores antigos já faziam: campo novo não
 *        quebra o rascunho, campo que saiu não fica assombrando.
 * @param semear o rascunho que ficou no `localStorage`, para a migração de uma vez só.
 *        Só é consultado quando a página nunca foi escrita no servidor.
 */
export function useRascunho<T extends object>(
  chave: string,
  interpretar: (guardado: unknown) => T,
  semear?: () => unknown
) {
  const pagina = useQuery({
    queryKey: paginaApi.chaveDaPagina(chave),
    queryFn: () => paginaApi.ler(chave),
    staleTime: Infinity,
  });

  const [valor, setValor] = useState<T | null>(null);

  /**
   * Distingue "a página acabou de carregar" de "há algo novo a gravar".
   *
   * <p>Sem isto, o primeiro render gravaria o que acabou de ser lido — um PUT que
   * ninguém pediu, disparado por uma leitura.
   */
  const paraGravar = useRef(false);

  const gravacao = useMutation({
    mutationFn: (conteudo: unknown) => paginaApi.salvar(chave, conteudo),
  });

  useEffect(() => {
    if (!pagina.data || valor) return;

    /*
     * `atualizadoEm` nulo é o sinal de "ninguém escreveu esta página", e é ele que
     * autoriza a semeadura. Olhar para o conteúdo vazio não serviria: uma página que o
     * time esvaziou de propósito seria semeada de novo a cada abertura, devolvendo o
     * rascunho que a pessoa acabou de apagar.
     */
    const nuncaEscrita = !pagina.data.atualizadoEm;
    const semente = nuncaEscrita && semear ? semear() : undefined;

    setValor(interpretar(semente ?? pagina.data.conteudo));
    if (semente !== undefined) paraGravar.current = true;
    // `interpretar` e `semear` são recriados a cada render da tela; segui-los
    // reexecutaria a leitura sem que nada tivesse mudado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagina.data, valor]);

  useEffect(() => {
    if (!valor || !paraGravar.current) return;
    const agendada = setTimeout(() => gravacao.mutate(valor), ATRASO_MS);
    return () => clearTimeout(agendada);
    // `gravacao` muda de identidade a cada render do react-query; incluí-lo
    // reagendaria a gravação sem que nada tivesse sido digitado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valor]);

  /** Muda o rascunho e agenda a gravação. Mesma forma de um `setState`. */
  function definir(atualiza: (antes: T) => T) {
    paraGravar.current = true;
    setValor((antes) => (antes ? atualiza(antes) : antes));
  }

  const estado: EstadoDoRascunho = {
    gravando: gravacao.isPending,
    erroAoGravar: gravacao.isError ? gravacao.error : null,
    atualizadoPor: pagina.data?.atualizadoPor,
  };

  return { valor, definir, estado, erroAoLer: pagina.error };
}

/**
 * O aviso de gravação, igual nas quatro telas.
 *
 * <p>Um componente só, e não uma frase copiada em cada página: elas dizem a mesma coisa,
 * e quatro cópias seriam quatro chances de uma delas continuar prometendo "guardado
 * neste navegador" depois de a tela ter parado de fazer isso.
 */
export function AvisoDeGravacao({ estado }: { estado: EstadoDoRascunho }) {
  if (estado.erroAoGravar) {
    return (
      <p className="gd-gravacao erro">
        Não consegui gravar:{' '}
        {estado.erroAoGravar instanceof Error
          ? estado.erroAoGravar.message
          : 'erro desconhecido'}
        . O que está na tela não se perdeu — escreva de novo para tentar outra vez.
      </p>
    );
  }
  if (estado.gravando) return <p className="gd-gravacao">Gravando…</p>;
  if (estado.atualizadoPor) {
    return (
      <p className="gd-gravacao">
        Gravado no GG Pilot — última escrita por <b>{estado.atualizadoPor}</b>.
      </p>
    );
  }
  return <p className="gd-gravacao">Gravado no GG Pilot, visível para o time.</p>;
}

/** Os nomes das páginas. Num lugar só, porque o servidor os cobra por igual. */
export const PAGINAS = {
  /** O documento da tela Ficha — bônus por atributo e fórmulas dos secundários. O nome
   *  vem de quando a tela se chamava Personagem; a chave não muda porque é o endereço
   *  do que o time já gravou. */
  personagem: 'personagem',
  /*
   * A chave `combate` (o rascunho antigo de etapas/ações/distâncias) saiu com a tela
   * que a usava — o documento segue no banco, sem tela (decisão do Theo, 22/08/2026).
   * A página Combate nova grava nas duas chaves abaixo.
   */
  combateResumo: 'combate-resumo',
  combateSeason2: 'combate-season2',
  /** Ações de combate — o catálogo (da planilha do Theo) numa página, o motor noutra. */
  acoesDeCombate: 'acoes-de-combate',
  acoesDeCombateSeason2: 'acoes-de-combate-season2',
  tierList: 'tier-list',
  /** A lore de cada perícia, na página Perícias. O resto da página é consulta ao mundo. */
  pericias: 'pericias',
  /** Compartilhada: a Ficha e a Tier List editam as mesmas dezoito rolagens. */
  rolagens: 'rolagens',
  cenasAcoesFluxo: 'cenas-acoes-fluxo',
  /** A aba Beta/Season 1 da página Personagem — o resumo dos épicos 3/4/5 + GDD. */
  personagemResumo: 'personagem-resumo',
  /** A aba Season 2+ da mesma página: o que ficou para depois. Documento próprio,
   *  porque as duas abas se gravam separadas — quadro é chave de primeiro nível, e
   *  duas abas na mesma página colidiriam nas chaves. */
  personagemSeason2: 'personagem-season2',
  /** Condições do Personagem — a ficha saiu numa página, o que acontece com ela noutra. */
  condicoesDoPersonagem: 'condicoes-do-personagem',
  condicoesDoPersonagemSeason2: 'condicoes-do-personagem-season2',
} as const;
